import React, { useState, useEffect, useRef, useMemo } from 'react';
import FilterSection from '../components/Download/FilterSection';
import EquipmentFilterSection from '../components/Download/EquipmentFilterSection';
import { useNavigate } from 'react-router-dom';
import {
  fetchDeployments,
  fetchVideos,
  fetchFilteredDeployments,
  fetchEstimate,
  fetchGnssDataZip,
} from '../services/api';
import GalleryView from '../components/Download/GalleryView';
import ListView from '../components/Download/ListView';
import { useIjrrDownload } from '../hooks/useIjrrDownload';
import { VideoFile, Deployment } from '../types/types';
import { extractDeployment } from '../utils/deployment';
import { useUser } from '../context/UserContext';
import { LINKS } from '../constants/links';

const trajectories = [
  'All',
  'Yellow',
  'Green',
  'Orange',
  'Red',
  'Blue',
  'Magenta',
];
const seasons = ['All', 'Summer', 'Fall', 'Winter', 'Spring'];
const sensors = [
  'All',
  'No Sensor',
  'Audio',
  'Basler',
  'ZedX',
  'Navtech',
  'RoboSense',
  'Leishen',
];
const conditions = ['All', 'Snow', 'Leafs', 'Dust', 'Tracks'];

export default function Download() {
  const [selected, setSelected] = useState<{ [key: string]: string[] }>({
    Trajectory: ['All'],
    Deployment: ['All'],
    Season: ['All'],
    Sensors: ['All'],
    Conditions: ['All'],
  });

  const lastSelectedWithoutDeployment = useRef<{ [key: string]: string[] }>({
    Trajectory: ['All'],
    Season: ['All'],
    Sensors: ['All'],
    Conditions: ['All'],
  });

  const [format, setFormat] = useState<'IJRR' | 'MCAP'>('IJRR');
  const [view, setView] = useState<'gallery' | 'list'>('gallery');
  const [isPreviewVideosRetrieved, setIsPreviewVideosRetrieved] =
    useState(false);
  const [deployments, setDeployments] = useState<string[]>([]);
  const [selectedDeployments, setSelectedDeployments] = useState<
    Record<string, boolean>
  >({});
  const [videoFiles, setVideoFiles] = useState<VideoFile[]>([]);
  const [filteredDeployments, setFilteredDeployments] = useState<Deployment[]>(
    []
  );
  const [isZedXSubmenuOpen, setIsZedXSubmenuOpen] = useState(false);

  const [liveEstimate, setLiveEstimate] = useState<{
    bytes: number;
    human: string;
  } | null>(null);
  const [isLiveEstimating, setIsLiveEstimating] = useState(false);

  const { user } = useUser();
  const [showGnssInfo, setShowGnssInfo] = useState(false);
  const navigate = useNavigate();
  const isFirstRender = useRef(true);

  const { handleBashScript } = useIjrrDownload({
    selected,
    userEmail: user?.email,
    format,
  });

  const isStereoActive = (cams: string[]) =>
    cams.includes('Stereo Left') || cams.includes('Stereo Right');

  const uniq = <T,>(arr: T[]) => Array.from(new Set(arr));

  const selectedSensorsForDisplay = useMemo(() => {
    const cams = selected.Sensors;
    if (cams.includes('All')) return ['All'];
    const others = cams.filter(
      (c) => c !== 'Stereo Left' && c !== 'Stereo Right'
    );
    if (isStereoActive(cams)) return uniq([...others, 'ZedX']);
    return others;
  }, [selected.Sensors]);

  const handleFormatChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFormat = e.target.value as 'IJRR' | 'MCAP';
    if (newFormat === 'MCAP') {
      setFormat('IJRR');
      return;
    }
    setFormat(newFormat);
  };

  const toggleItem = (category: string, item: string) => {
    setSelected((prev) => {
      const current = prev[category] || [];
      let updated: string[];
      if (item === 'All' || item === 'No Sensor') {
        updated =
          current.includes(item) && item === 'No Sensor' ? ['All'] : [item];
      } else {
        updated = current.includes(item)
          ? current.filter((i) => i !== item)
          : [...current.filter((i) => i !== 'All' && i !== 'No Sensor'), item];
      }
      const cleanedDeployment = (prev.Deployment || []).filter(
        (d) => !d.startsWith('Deployment')
      );
      return { ...prev, [category]: updated, Deployment: cleanedDeployment };
    });
  };

  const toggleStereo = () => {
    setSelected((prev) => {
      let cams = prev.Sensors.filter((c) => c !== 'All' && c !== 'No Sensor');
      const active = isStereoActive(cams);
      if (active) {
        let updated = cams.filter(
          (c) => c !== 'Stereo Left' && c !== 'Stereo Right'
        );
        if (updated.length === 0) updated = ['All'];
        return { ...prev, Sensors: updated };
      } else {
        const cleanedDeployment = (prev.Deployment || []).filter(
          (d) => !d.startsWith('Deployment')
        );
        return {
          ...prev,
          Sensors: uniq([...cams, 'Stereo Left', 'Stereo Right']),
          Deployment: cleanedDeployment,
        };
      }
    });
  };

  const handleStereoDropdownChange = (value: 'Left' | 'Right' | 'Both') => {
    setSelected((prev) => {
      let cams = prev.Sensors.filter(
        (c) =>
          c !== 'All' &&
          c !== 'No Sensor' &&
          c !== 'Stereo Left' &&
          c !== 'Stereo Right'
      );
      if (value.includes('Left')) cams.push('Stereo Left');
      if (value.includes('Right')) cams.push('Stereo Right');
      const cleanedDeployment = (prev.Deployment || []).filter(
        (d) => !d.startsWith('Deployment')
      );
      return { ...prev, Sensors: uniq(cams), Deployment: cleanedDeployment };
    });
  };

  const handleDownload = () => {
    handleBashScript();
  };

  useEffect(() => {
    fetchDeployments()
      .then((data) => {
        const deploymentDates = data.map((d: any) => d.date);
        setDeployments(deploymentDates);
        const initialSelected = deploymentDates.reduce(
          (acc: Record<string, boolean>, date: string) => {
            acc[date] = true;
            return acc;
          },
          {}
        );
        setSelectedDeployments(initialSelected);
      })
      .catch((err) => console.error('Error retrieving deployments:', err));
  }, []);

  useEffect(() => {
    const selectedWithoutDeployment = { ...selected };
    delete selectedWithoutDeployment.Deployment;

    if (isFirstRender.current) isFirstRender.current = false;
    else {
      const hasChanged =
        JSON.stringify(selectedWithoutDeployment) !==
        JSON.stringify(lastSelectedWithoutDeployment.current);
      if (!hasChanged) return;
    }

    lastSelectedWithoutDeployment.current = selectedWithoutDeployment;
    setIsPreviewVideosRetrieved(false);
    (async () => {
      try {
        const data = await fetchVideos(selected);
        setVideoFiles(data.files);
        setIsPreviewVideosRetrieved(true);
      } catch (err) {
        console.error('Error fetching videos:', err);
      }
    })();
  }, [selected]);

  useEffect(() => {
    const fetchData = async () => {
      const filtered = await fetchFilteredDeployments(selected);
      setFilteredDeployments(filtered);
    };
    fetchData();
  }, [selected]);

  useEffect(() => {
    if (videoFiles.length > 0) {
      const deploymentSet = new Set<string>();
      videoFiles.forEach((file) =>
        deploymentSet.add(extractDeployment(file.url))
      );
      setSelectedDeployments((prev) => {
        const newState = { ...prev };
        deploymentSet.forEach((d) => {
          if (!(d in newState)) newState[d] = true;
        });
        return newState;
      });
    }
  }, [view, videoFiles]);

  useEffect(() => {
    const activeDeployments = Object.entries(selectedDeployments)
      .filter(([_, isSelected]) => isSelected)
      .map(([date]) => date);

    setSelected((prev) => {
      const cleanedDeployment = (prev.Deployment || []).filter(
        (d) => !d.startsWith('Deployment')
      );
      return {
        ...prev,
        Deployment:
          activeDeployments.length > 0
            ? activeDeployments
            : cleanedDeployment.length > 0
              ? cleanedDeployment
              : ['All'],
      };
    });
  }, [selectedDeployments]);

  useEffect(() => {
    const handleZedXSelect = (e: any) => {
      const side = e.detail as 'Left' | 'Right';
      handleStereoDropdownChange(side);
    };
    window.addEventListener('zedx-select', handleZedXSelect);
    return () => window.removeEventListener('zedx-select', handleZedXSelect);
  }, []);

  useEffect(() => {
    setIsLiveEstimating(true);
    setLiveEstimate(null);
    const timeout = setTimeout(async () => {
      try {
        const est = await fetchEstimate(selected);
        setLiveEstimate(est);
      } catch {
        setLiveEstimate(null);
      } finally {
        setIsLiveEstimating(false);
      }
    }, 400);
    return () => clearTimeout(timeout);
  }, [selected]);

  return (
    <div className="min-h-screen bg-white dark:bg-darkbg text-gray-900 dark:text-white py-12 px-6 max-w-6xl mx-auto space-y-6">
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-2.5">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-4 h-4 shrink-0"
          >
            <path
              fillRule="evenodd"
              d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
          MCAP download is not yet available. Please use the Human readable
          format in the meantime.
        </div>
      </div>

      <div className="space-y-4 text-center">
        <p className="text-lg font-medium">
          Check the boxes of sequences you want to download, then click the
          download button at the bottom.
        </p>
      </div>

      <div className="flex flex-wrap justify-center items-start gap-4">
        {/* Format selector + Requires */}
        <div className="flex flex-col items-center gap-2">
          <div>
            <label className="font-medium mr-4">Format:</label>
            <select
              value={format}
              onChange={handleFormatChange}
              className="bg-gray-200 dark:bg-gray-700 px-4 py-2 rounded"
            >
              <option value="IJRR">Human readable</option>
              <option value="MCAP">MCAP (coming soon)</option>
            </select>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span>Requires:</span>
            <a
              href="https://aws.amazon.com/cli/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-300 dark:border-neutral-600 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              AWS CLI
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                className="w-3 h-3 opacity-60"
              >
                <path d="M2 10L10 2M10 2H5M10 2v5" />
              </svg>
            </a>
            {format === 'MCAP' && (
              <>
                <span className="text-gray-300 dark:text-neutral-600">+</span>
                <a
                  href="https://docs.docker.com/get-docker/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-300 dark:border-neutral-600 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  Docker
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className="w-3 h-3 opacity-60"
                  >
                    <path d="M2 10L10 2M10 2H5M10 2v5" />
                  </svg>
                </a>
              </>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="hidden sm:block w-px self-stretch bg-gray-300 dark:bg-neutral-600 mx-1" />

        {/* GNSS compact row */}
        <div className="flex items-center gap-2">
          {/* Signal icon */}
          <div className="w-7 h-7 rounded-md bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center shrink-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.8}
              stroke="currentColor"
              className="w-4 h-4 text-emerald-600 dark:text-emerald-400"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z"
              />
            </svg>
          </div>

          <div className="leading-tight">
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">
              GNSS Raw Logs
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              RINEX · 9 deployments · ~4.3 GB
            </p>
          </div>

          {/* Info icon + tooltip */}
          <div className="relative">
            <button
              onMouseEnter={() => setShowGnssInfo(true)}
              onMouseLeave={() => setShowGnssInfo(false)}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition"
              aria-label="More info about GNSS data"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                />
              </svg>
            </button>
            {showGnssInfo && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl z-[9999] leading-relaxed">
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-gray-900" />
                <p className="font-semibold mb-1">GNSS Raw Logs (RINEX)</p>
                <p>
                  RINEX files from the{' '}
                  <span className="text-emerald-400 font-medium">
                    Reach RS3 base station
                  </span>{' '}
                  (static antenna) and{' '}
                  <span className="text-emerald-400 font-medium">
                    3 rover receivers
                  </span>{' '}
                  (M2 front, left, right) mounted on the UGV.
                </p>
                <p className="mt-1.5 text-gray-300">
                  Used for PPK ground truth generation. Nov21, Jan29 and Jun26
                  deployments are excluded (reserved for evaluation).
                </p>
              </div>
            )}
          </div>

          <button
            onClick={() => fetchGnssDataZip()}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow transition"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-3.5 h-3.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M7.5 12l4.5 4.5m0 0l4.5-4.5m-4.5 4.5V3"
              />
            </svg>
            Download
          </button>
        </div>
      </div>

      <div className="space-y-10">
        <>
          <FilterSection
            title="Trajectory"
            items={trajectories}
            selected={selected['Trajectory']}
            onToggle={(item) => toggleItem('Trajectory', item)}
          />
          <FilterSection
            title="Season"
            items={seasons}
            selected={selected['Season']}
            onToggle={(item) => toggleItem('Season', item)}
          />
          <FilterSection
            title="Conditions"
            items={conditions}
            selected={selected['Conditions']}
            onToggle={(item) => toggleItem('Conditions', item)}
          />
        </>
        <EquipmentFilterSection
          title="Sensors"
          items={sensors}
          selected={selectedSensorsForDisplay}
          onToggle={(item) => {
            if (item === 'ZedX') toggleStereo();
            else toggleItem('Sensors', item);
          }}
        />
      </div>

      <div
        className={`text-center text-sm text-gray-600 dark:text-gray-400 space-y-1 ${isZedXSubmenuOpen ? 'mt-12' : ''}`}
      >
        <p>
          Each trajectory also includes IMU and wheel odometry data, along with
          intrinsic and extrinsic calibrations.
        </p>
        <p>
          <button
            onClick={() => navigate(LINKS.SENSORS)}
            className="text-blue-600 hover:text-blue-800 underline font-medium"
          >
            Learn about data formats
          </button>
        </p>
      </div>

      <div className="text-center pt-4">
        <label htmlFor="view" className="mr-2 font-medium">
          Select View:
        </label>
        <select
          id="view"
          value={view}
          onChange={(e) => setView(e.target.value as 'gallery' | 'list')}
          className="bg-gray-200 dark:bg-gray-700 px-4 py-2 rounded"
        >
          <option value="gallery">Gallery View</option>
          <option value="list">List View</option>
        </select>
      </div>

      {view === 'gallery' ? (
        <GalleryView
          isPreviewVideosRetrieved={isPreviewVideosRetrieved}
          checkedDeployments={selectedDeployments}
          setCheckedDeployments={setSelectedDeployments}
          filteredDeployments={filteredDeployments}
        />
      ) : (
        <ListView
          isPreviewVideosRetrieved={isPreviewVideosRetrieved}
          checkedItems={selectedDeployments}
          setCheckedItems={setSelectedDeployments}
          filteredDeployments={filteredDeployments}
        />
      )}

      {/* Bouton Download + estimated size */}
      <div className="text-center space-y-2 pt-6">
        <button
          onClick={handleDownload}
          className="bg-blue-600 text-white font-semibold px-6 py-2 rounded shadow hover:bg-blue-700 transition inline-flex items-center justify-center"
        >
          Download
        </button>

        {/* Estimated size sous le bouton */}
        <div className="flex items-center justify-center gap-1.5 h-5 text-sm text-gray-500 dark:text-gray-400">
          {isLiveEstimating ? (
            <>
              <svg
                className="animate-spin w-3.5 h-3.5 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                />
              </svg>
              <span className="animate-pulse">Calculating size…</span>
            </>
          ) : liveEstimate ? (
            <span>
              The downloaded script will generate an estimate of{' '}
              <span className="font-semibold text-gray-700 dark:text-gray-200">
                {liveEstimate.human}
              </span>{' '}
              of data.
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
